import axios, { AxiosError, AxiosResponse } from 'axios'
import { enqueueSnackbar } from 'notistack'
import { WebStorageStateStore } from 'oidc-client-ts'
import type { JSX } from 'react'
import { useEffect, useRef, useState } from 'react'
import type { AuthContextProps } from 'react-oidc-context'
import { AuthProvider, hasAuthParams, useAuth } from 'react-oidc-context'
import { samApiClient, samFotaApiClient } from '@/api'
import { APP_CONFIG } from '../../env-cmdrc'

export const TOKEN_NAMESPACE = 'DI_AC_TOKEN'
export const ID_TOKEN_NAMESPACE = 'DI_ID_TOKEN'

const MAX_SILENT_RENEWAL_FAILURES = 3
const LOGOUT_FLAG_RESET_DELAY = 1000

type Props = {
  children: string | JSX.Element | JSX.Element[]
}

type PromiseRef = {
  current: Promise<unknown> | null
}

type BooleanRef = {
  current: boolean
}

type RetryMapRef = {
  current: Map<string, number>
}

const signInCallback = (): void => {
  window.history.replaceState({}, document.title, window.location.pathname)
}

const CustomAuthProvider = ({ children }: Props): JSX.Element => {
  return (
    <AuthProvider
      automaticSilentRenew={true}
      userStore={new WebStorageStateStore()}
      authority={APP_CONFIG.IDP_URL}
      client_id={APP_CONFIG.IDP_CLIENT_ID}
      redirect_uri={APP_CONFIG.IDP_REDIRECT_URL}
      post_logout_redirect_uri={APP_CONFIG.IDP_REDIRECT_URL}
      scope="openid profile api"
      response_type="code"
      loadUserInfo={true}
      onSigninCallback={signInCallback}
      silent_redirect_uri={window.location.origin + window.location.pathname}
      accessTokenExpiringNotificationTimeInSeconds={120}
      extraQueryParams={{
        max_age: '28800',
      }}
    >
      {children}
    </AuthProvider>
  )
}

const AutoAuthWrapper = ({ children }: Props): JSX.Element => {
  const auth = useAuth()
  const interceptorsInitialized = useRef(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isSilentRenewing, setIsSilentRenewing] = useState(false)
  const [authReady, setAuthReady] = useState(false)

  const renewalInProgress = useRef<Promise<unknown> | null>(null)
  const silentRenewFailureCount = useRef(0)
  const manualLogoutInProgress = useRef(false)
  const isHandling403 = useRef(false)
  const retryAttempts = useRef(new Map<string, number>())

  // Wait for auth library to fully initialize
  useEffect(() => {
    if (!auth.isLoading) {
      // Only mark as ready when loading is complete
      setAuthReady(true)
    }
  }, [auth.isLoading])

  useEffect(() => {
    if (!interceptorsInitialized.current) {
      setupInterceptors(
        auth,
        setIsSilentRenewing,
        renewalInProgress,
        manualLogoutInProgress,
        isHandling403,
        retryAttempts,
      )
      interceptorsInitialized.current = true
    }
  }, [auth])

  useEffect(() => {
    // Only proceed if auth is ready (library finished initializing)
    if (!authReady) {
      return
    }

    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading
    ) {
      auth.signinRedirect().catch((error: Error) => {
        console.error('Failed to initiate signin:', error)
      })
    } else if (auth.isAuthenticated && auth.user?.access_token) {
      // Only set initial load false when truly authenticated with valid token
      setIsInitialLoad(false)
    }
  }, [auth, authReady])

  useEffect(() => {
    if (manualLogoutInProgress.current || isHandling403.current) {
      return
    }

    if (!auth.user?.access_token) {
      return
    }

    try {
      const currentToken = localStorage.getItem(TOKEN_NAMESPACE)

      // Only update if token actually changed
      if (currentToken !== auth.user.access_token) {
        localStorage.setItem(TOKEN_NAMESPACE, auth.user.access_token)

        if (auth.user.id_token) {
          localStorage.setItem(ID_TOKEN_NAMESPACE, auth.user.id_token)
        }
      }
    } catch (error) {
      console.error('Failed to store tokens in localStorage:', error)
    }
  }, [auth.user])

  useEffect(() => {
    const handleUserLoaded = (): void => {
      setIsSilentRenewing(false)
      renewalInProgress.current = null
      silentRenewFailureCount.current = 0
    }

    const handleSilentRenewError = (error: Error): void => {
      console.error('Silent renewal failed:', error)
      setIsSilentRenewing(false)
      renewalInProgress.current = null

      if (isHandling403.current) {
        return
      }

      silentRenewFailureCount.current++

      if (silentRenewFailureCount.current >= MAX_SILENT_RENEWAL_FAILURES) {
        const isOnAuthPage =
          window.location.pathname.includes('login') ||
          window.location.pathname.includes('auth') ||
          window.location.pathname.includes('callback')

        if (!isOnAuthPage) {
          sessionStorage.setItem('returnUrl', window.location.href)
          auth.signinRedirect().catch((signinError: Error) => {
            console.error('Failed to redirect to signin:', signinError)
          })
        }
      }
    }

    const handleUserSignedOut = (): void => {
      renewalInProgress.current = null
      silentRenewFailureCount.current = 0

      if (!manualLogoutInProgress.current && !isHandling403.current) {
        localStorage.removeItem(TOKEN_NAMESPACE)
        localStorage.removeItem(ID_TOKEN_NAMESPACE)
      }
    }

    auth.events?.addUserLoaded(handleUserLoaded)
    auth.events?.addSilentRenewError(handleSilentRenewError)
    auth.events?.addUserSignedOut(handleUserSignedOut)

    return (): void => {
      auth.events?.removeUserLoaded(handleUserLoaded)
      auth.events?.removeSilentRenewError(handleSilentRenewError)
      auth.events?.removeUserSignedOut(handleUserSignedOut)
    }
  }, [auth])

  // Show loading until auth library has initialized
  if (!authReady || (auth.isLoading && isInitialLoad)) {
    return <p>Loading...</p>
  }

  if (auth.activeNavigator && isInitialLoad && !auth.isAuthenticated) {
    return <div>Signing you in/out...</div>
  }

  if (!auth.isAuthenticated && !auth.isLoading && !isInitialLoad) {
    return <div>Unable to log in</div>
  }

  // Strict authentication check
  // Only render children when ALL conditions are met:
  // Auth library initialized (authReady)
  // User is authenticated
  // User object exists
  // Access token exists
  if (!authReady || !auth.isAuthenticated || !auth.user?.access_token) {
    return <p>Checking authentication...</p>
  }

  return (
    <>
      {isSilentRenewing && (
        <div
          style={{
            position: 'fixed',
            top: 10,
            right: 10,
            background: 'rgba(0,172,193,0.9)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            fontSize: '13px',
            zIndex: 9999,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          Refreshing session...
        </div>
      )}
      {children}
    </>
  )
}

const apiClients = [samApiClient, samFotaApiClient]

const getIdTokenForLogout = (auth: AuthContextProps): string | null => {
  if (auth.user?.id_token) {
    return auth.user.id_token
  }

  const storedToken = localStorage.getItem(ID_TOKEN_NAMESPACE)
  if (storedToken) {
    return storedToken
  }

  return null
}

const performSilentRenewal = async (
  auth: AuthContextProps,
  renewalInProgress: PromiseRef,
  isHandling403: BooleanRef,
): Promise<unknown> => {
  if (isHandling403.current) {
    throw new Error('Silent renewal blocked during logout')
  }

  if (renewalInProgress.current) {
    return await renewalInProgress.current
  }

  try {
    renewalInProgress.current = auth.signinSilent()
    const user = await renewalInProgress.current

    if (!user?.access_token) {
      throw new Error('No access token returned from silent renewal')
    }

    if (!isHandling403.current) {
      localStorage.setItem(TOKEN_NAMESPACE, user.access_token)
      if (user.id_token) {
        localStorage.setItem(ID_TOKEN_NAMESPACE, user.id_token)
      }
    }

    return user
  } finally {
    renewalInProgress.current = null
  }
}

const handle401Error = async (
  error: AxiosError,
  auth: AuthContextProps,
  setIsSilentRenewing: (renewing: boolean) => void,
  renewalInProgress: PromiseRef,
  isHandling403: BooleanRef,
  retryAttempts: RetryMapRef,
): Promise<AxiosResponse> => {
  if (isHandling403.current) {
    throw error
  }

  if (!auth.isAuthenticated || !auth.user) {
    localStorage.removeItem(TOKEN_NAMESPACE)
    localStorage.removeItem(ID_TOKEN_NAMESPACE)
    sessionStorage.setItem('returnUrl', window.location.href)

    auth.signinRedirect().catch((redirectError: Error) => {
      console.error('Failed to redirect to signin:', redirectError)
    })

    throw error
  }

  const requestId = `${error.config?.method || 'GET'}_${error.config?.url || 'unknown'}`
  const currentRetries = retryAttempts.current.get(requestId) || 0

  if (currentRetries >= 1) {
    retryAttempts.current.delete(requestId)
    sessionStorage.setItem('returnUrl', window.location.href)

    auth.signinRedirect().catch((redirectError: Error) => {
      console.error('Failed to redirect after max retries:', redirectError)
    })

    throw error
  }

  try {
    setIsSilentRenewing(true)
    retryAttempts.current.set(requestId, currentRetries + 1)

    const user = await performSilentRenewal(
      auth,
      renewalInProgress,
      isHandling403,
    )

    if (
      user &&
      typeof user === 'object' &&
      'access_token' in user &&
      error.config
    ) {
      error.config.headers = error.config.headers || {}
      error.config.headers['Authorization'] = `Bearer ${user.access_token}`

      const response = await axios.request(error.config)

      retryAttempts.current.delete(requestId)

      return response
    }

    throw new Error('No valid access token available')
  } catch (renewalError) {
    console.error('Failed to renew token for 401:', renewalError)
    // will not delete counter here since it prevents retry loops
    // By not deleting, the next 401 for same request will hit max retries check

    sessionStorage.setItem('returnUrl', window.location.href)
    auth.signinRedirect().catch((redirectError: Error) => {
      console.error('Failed to redirect after renewal failure:', redirectError)
    })

    throw error
  } finally {
    setIsSilentRenewing(false)
    renewalInProgress.current = null
  }
}

const handle403Error = async (
  error: AxiosError,
  auth: AuthContextProps,
  manualLogoutInProgress: BooleanRef,
  isHandling403: BooleanRef,
): Promise<void> => {
  if (isHandling403.current) {
    return
  }

  isHandling403.current = true
  manualLogoutInProgress.current = true

  enqueueSnackbar(
    'Access denied: You do not have permission to access P&M tool. You will be signed out.',
    {
      variant: 'error',
      autoHideDuration: 8000,
      persist: false,
    },
  )

  try {
    const idToken = getIdTokenForLogout(auth)
    sessionStorage.setItem('returnUrl', window.location.href)

    localStorage.removeItem(TOKEN_NAMESPACE)
    localStorage.removeItem(ID_TOKEN_NAMESPACE)

    if (idToken) {
      await auth.signoutRedirect({
        post_logout_redirect_uri: APP_CONFIG.IDP_REDIRECT_URL,
        id_token_hint: idToken,
      })
    } else {
      await auth.removeUser()
      window.location.href = APP_CONFIG.IDP_REDIRECT_URL
    }
  } catch (signoutError) {
    console.error('IDP logout failed, using direct redirect:', signoutError)

    try {
      await auth.removeUser()
    } catch {
      // Ignore removeUser errors
    }

    try {
      const url = new URL(APP_CONFIG.IDP_REDIRECT_URL)
      window.location.href = url.origin + url.pathname
    } catch {
      window.location.reload()
    }
  } finally {
    setTimeout(() => {
      manualLogoutInProgress.current = false
      isHandling403.current = false
    }, LOGOUT_FLAG_RESET_DELAY)
  }
}

const handleOtherErrors = (status: number, error: AxiosError): void => {
  if (status >= 400 && status < 500 && status !== 409 && status !== 503) {
    const data = error.response?.data
    const message =
      typeof data === 'string' && data.length > 0
        ? data
        : ((data as { message?: string } | undefined)?.message ??
          `Request failed: ${status}`)
    enqueueSnackbar(message, {
      variant: 'error',
    })
  }
}

const createResponseInterceptor = (
  auth: AuthContextProps,
  setIsSilentRenewing: (renewing: boolean) => void,
  renewalInProgress: PromiseRef,
  manualLogoutInProgress: BooleanRef,
  isHandling403: BooleanRef,
  retryAttempts: RetryMapRef,
) => {
  const successHandler = (response: AxiosResponse): AxiosResponse => response

  const errorHandler = async (error: AxiosError): Promise<AxiosResponse> => {
    if (axios.isCancel(error)) {
      throw error
    }

    const { status } = error.response || { status: 0 }

    if (status === 401) {
      return handle401Error(
        error,
        auth,
        setIsSilentRenewing,
        renewalInProgress,
        isHandling403,
        retryAttempts,
      )
    }

    if (status === 403) {
      await handle403Error(error, auth, manualLogoutInProgress, isHandling403)
      throw error
    }

    handleOtherErrors(status, error)
    throw error
  }

  return [successHandler, errorHandler] as const
}

const setupInterceptors = (
  auth: AuthContextProps,
  setIsSilentRenewing: (renewing: boolean) => void,
  renewalInProgress: PromiseRef,
  manualLogoutInProgress: BooleanRef,
  isHandling403: BooleanRef,
  retryAttempts: RetryMapRef,
): void => {
  for (const client of apiClients) {
    const [onFulfilled, onRejected] = createResponseInterceptor(
      auth,
      setIsSilentRenewing,
      renewalInProgress,
      manualLogoutInProgress,
      isHandling403,
      retryAttempts,
    )
    client.interceptors.response.use(onFulfilled, onRejected)
  }
}

export type Context = {
  isAuthenticated: boolean
  isLoading: boolean
  profile?: Profile
  signOut: () => Promise<void>
  signInOIDC?: () => Promise<void>
}

export type Profile = {
  name?: string
  company?: { name: string; uuid: string }[]
  picture?: string
  productionOnly?: boolean
}

export { CustomAuthProvider, AutoAuthWrapper }
